import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Video, Sparkles, Film, Settings2, Trash2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import VisionStudioGate from "@/components/vision/VisionStudioGate";
import { visionApi, PROJECT_TYPE_META, STATUS_META } from "@/lib/visionApi";
import { appConfirm } from "@/components/AppDialogHost";

const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString("en-US", { month:"short", day:"2-digit", hour:"2-digit", minute:"2-digit" }); } catch { return iso; }
};

const NewProjectModal = ({ onClose, onCreated }) => {
  const [title, setTitle] = useState("");
  const [type, setType] = useState("station_promo");
  const [duration, setDuration] = useState(30);
  const [aspect, setAspect] = useState("9:16");
  const [provider, setProvider] = useState("veo");
  const [brief, setBrief] = useState("");
  const [working, setWorking] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setWorking(true);
    try {
      const p = await visionApi.createProject({
        title: title.trim(),
        project_type: type,
        duration_seconds: Number(duration) || 30,
        aspect,
        provider,
        brief: brief.trim(),
      });
      toast.success("Project created");
      onCreated(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to create");
    } finally { setWorking(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-up" data-testid="new-project-modal" onClick={onClose}>
      <div className="bg-bg-surface border border-line rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="type-section-heading text-ink-primary mb-1">New Vision Project</h3>
        <div className="type-body text-ink-muted mb-5">AI will generate your script and storyboard — you keep full edit control.</div>

        <label className="block mb-4">
          <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Title</div>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)}
            data-testid="new-project-title"
            placeholder="e.g. Seductive Vibes Weekend Mix Promo"
            className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 focus:border-brand-amber focus:outline-none"
          />
        </label>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 max-h-64 overflow-y-auto pr-1">
          {Object.entries(PROJECT_TYPE_META).map(([key, meta]) => (
            <button
              key={key} onClick={() => setType(key)}
              data-testid={`pt-${key}`}
              className={`text-left rounded-lg border p-3 transition ${type === key ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated hover:border-brand-blue/40"}`}
            >
              <div className="text-lg">{meta.emoji}</div>
              <div className="type-list-title text-ink-primary">{meta.label}</div>
              <div className="type-list-subtitle text-ink-muted line-clamp-2">{meta.hint}</div>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <label>
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Duration (s)</div>
            <input type="number" min="10" max="600" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="new-project-duration" className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 focus:border-brand-amber focus:outline-none" />
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Aspect</div>
            <select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="new-project-aspect" className="w-full appearance-none rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 pr-8 focus:border-brand-amber focus:outline-none bg-[length:14px] bg-[right_10px_center] bg-no-repeat" style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A1A1AA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")" }}>
              <option value="9:16">9:16 (vertical)</option>
              <option value="16:9">16:9 (landscape)</option>
              <option value="1:1">1:1 (square)</option>
            </select>
          </label>
          <label>
            <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Provider</div>
            <select value={provider} onChange={(e) => setProvider(e.target.value)} data-testid="new-project-provider" className="w-full appearance-none rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 pr-8 focus:border-brand-amber focus:outline-none bg-no-repeat bg-[length:14px] bg-[right_10px_center]" style={{ backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A1A1AA' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e\")" }}>
              <option value="veo">Google Veo</option>
              <option value="sora">OpenAI Sora 2</option>
              <option value="runway">Runway Gen-3</option>
              <option value="pika">Pika 2.0</option>
              <option value="luma">Luma Dream Machine</option>
              <option value="kling">Kling AI</option>
              <option value="hailuo">Hailuo AI</option>
              <option value="minimax">Minimax</option>
              <option value="flow3">Flow3</option>
              <option value="flova_ai">Flova.ai</option>
              <option value="gemini_video">Gemini Video</option>
            </select>
          </label>
        </div>

        <label className="block mb-5">
          <div className="text-xs uppercase tracking-wider text-ink-muted mb-1">Creative Brief (optional)</div>
          <textarea
            value={brief} onChange={(e) => setBrief(e.target.value)}
            data-testid="new-project-brief" rows={3}
            placeholder="What story do you want to tell? Mood, tone, audience, key beats..."
            className="w-full rounded-md border border-line bg-bg-elevated text-ink-primary placeholder:text-ink-muted px-3 py-2 focus:border-brand-amber focus:outline-none"
          />
        </label>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-line bg-bg-elevated text-sm hover:border-brand-onair transition">Cancel</button>
          <button
            onClick={submit} disabled={working}
            data-testid="new-project-submit"
            className="px-5 py-2 rounded-md bg-brand-amber text-bg type-button-label inline-flex items-center gap-2 disabled:opacity-50"
          >
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {working ? "Creating…" : "Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function VisionStudio() {
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const a = await visionApi.access();
        setAccess(a);
        if (a.allowed) {
          const [p, pr] = await Promise.all([visionApi.listProjects(), visionApi.providers()]);
          setProjects(p.items || []);
          setProviders(pr.providers || []);
        }
      } catch (e) { toast.error("Could not load Vision Studio"); }
      finally { setLoading(false); }
    })();
  }, []);

  const remove = async (id, title) => {
    if (!await appConfirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await visionApi.deleteProject(id);
      setProjects((ps) => ps.filter((p) => p.project_id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) {
    return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading Vision Studio…</div>;
  }
  if (!access?.allowed) return <VisionStudioGate access={access} />;

  const providersConnected = providers.filter((p) => p.connected).length;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="vision-studio-page">
      <PageHeader
        eyebrow="// VISION STUDIO"
        title="BroadcastFlow Vision Studio."
        description="Turn news, podcasts and station content into broadcast-grade AI video — script → storyboard → narration → render → publish."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-projects">
          <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Recent Projects</div>
          <div className="font-display text-3xl font-bold mt-1">{projects.length}</div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-providers">
          <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Providers Connected</div>
          <div className="font-display text-3xl font-bold mt-1">{providersConnected}<span className="text-base text-ink-muted">/{providers.length}</span></div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-renders">
          <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Renders This Month</div>
          <div className="font-display text-3xl font-bold mt-1">0</div>
        </div>
        <div className="rounded-xl border border-line bg-bg-surface p-4" data-testid="kpi-plan">
          <div className="type-card-meta-label uppercase tracking-wider text-ink-muted">Plan</div>
          <div className="font-display text-lg font-bold mt-2 uppercase">{(access.plan || "free").replace("_", " ")}</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="type-section-heading text-ink-primary">Recent Projects</h3>
        <div className="flex gap-2">
          <button
            onClick={() => navigate("/dashboard/vision-studio/providers")}
            data-testid="open-providers"
            className="inline-flex items-center gap-1 type-button-label px-3 py-2 rounded-md border border-line bg-bg-elevated hover:border-brand-blue transition"
          >
            <Settings2 className="h-3.5 w-3.5" /> AI Video Providers
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="open-new-project"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-amber text-bg type-button-label hover:bg-brand-amber/90 transition"
          >
            <Plus className="h-4 w-4" /> New Project
          </button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface p-10 text-center" data-testid="empty-projects">
          <Video className="h-12 w-12 mx-auto text-ink-muted mb-3" />
          <h3 className="type-section-heading text-ink-primary mb-1">No projects yet</h3>
          <div className="type-body text-ink-muted mb-4">Create your first AI video project and watch BroadcastFlow generate the script, storyboard and visuals.</div>
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-brand-amber text-bg type-button-label hover:bg-brand-amber/90 transition"
          >
            <Sparkles className="h-4 w-4" /> Start a Project
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => {
            const meta = PROJECT_TYPE_META[p.project_type] || { label: p.project_type, emoji: "🎬" };
            const status = STATUS_META[p.status] || STATUS_META.draft;
            return (
              <div key={p.project_id} className="rounded-xl border border-line bg-bg-surface p-4 hover:border-brand-blue transition cursor-pointer group relative" data-testid={`project-card-${p.project_id}`}>
                <div onClick={() => navigate(`/dashboard/vision-studio/project/${p.project_id}`)}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl">{meta.emoji}</div>
                    <div className={`type-status-badge uppercase tracking-wider px-2 py-0.5 rounded ${status.bg} ${status.color}`}>{status.label}</div>
                  </div>
                  <div className="type-list-title text-ink-primary mb-1 group-hover:text-brand-amber transition">{p.title}</div>
                  <div className="type-list-subtitle text-ink-muted mb-2">{meta.label} · {p.duration_seconds}s · {p.aspect} · {p.provider}</div>
                  <div className="type-timestamp text-ink-muted flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTs(p.updated_at)}</div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(p.project_id, p.title); }}
                  data-testid={`delete-${p.project_id}`}
                  className="absolute top-3 right-12 opacity-0 group-hover:opacity-100 text-ink-muted hover:text-brand-onair transition p-1"
                  aria-label="Delete project"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 rounded-xl border border-line bg-bg-surface p-5">
        <h3 className="type-section-heading text-ink-primary mb-3 flex items-center gap-2"><Film className="h-5 w-5 text-brand-blue" /> AI Video Providers</h3>
        <div className="type-body text-ink-muted mb-3">Connect your own API keys for any of these providers. Sora is available immediately via the Emergent LLM key.</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {providers.map((p) => (
            <div key={p.key} data-testid={`provider-pill-${p.key}`} className={`rounded-lg border p-2.5 type-list-subtitle flex items-center justify-between ${p.connected ? "border-emerald-500/40 bg-emerald-500/5" : "border-line bg-bg-elevated"}`}>
              <span className="type-list-title text-ink-primary">{p.label}</span>
              <span className={p.connected ? "text-emerald-300" : "text-ink-muted"}>{p.connected ? "✓" : "—"}</span>
            </div>
          ))}
        </div>
      </div>

      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={(p) => {
        setShowNew(false);
        navigate(`/dashboard/vision-studio/project/${p.project_id}`);
      }} />}
    </div>
  );
}
